import React, { ReactElement, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions, useHasPermission, useHasAnyPermission, useHasAllPermissions } from '@/contexts/PermissionContext';

// Base PermissionGuard component for flexible permission checking
interface PermissionGuardProps {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean; // For permissions array: true = AND, false = OR (default)
  fallback?: ReactNode;
  redirectTo?: string;
  children: ReactNode;
}

export function PermissionGuard({
  permission,
  permissions = [],
  requireAll = false,
  fallback = null,
  redirectTo,
  children,
}: PermissionGuardProps) {
  const { loading } = usePermissions();
  const location = useLocation();

  // Single permission check
  const { hasPermission: hasSinglePermission } = useHasPermission(permission || '');
  
  // Multiple permissions check
  const { hasPermission: hasAnyPerm } = useHasAnyPermission(permissions);
  const { hasPermission: hasAllPerms } = useHasAllPermissions(permissions);

  // Show loading while permissions are being fetched
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Determine if user has required permissions
  let hasAccess = false;

  if (permission) {
    // Single permission check
    hasAccess = hasSinglePermission;
  } else if (permissions.length > 0) {
    // Multiple permissions check
    hasAccess = requireAll ? hasAllPerms : hasAnyPerm;
  } else {
    // No permissions specified - deny access for security
    hasAccess = false;
  }

  if (!hasAccess) {
    if (redirectTo) {
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

// Specialized guards for common use cases based on your plan

// High-Impact Routes (Phase 4.1)
interface ReportingGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function ReportingGuard({ children, fallback }: ReportingGuardProps) {
  return (
    <PermissionGuard 
      permission="reporting.admin"
      fallback={fallback}
      redirectTo="/"
    >
      {children}
    </PermissionGuard>
  );
}

interface SettingsGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function SettingsGuard({ children, fallback }: SettingsGuardProps) {
  return (
    <PermissionGuard 
      permissions={["manage_settings", "system.override"]}
      requireAll={false}
      fallback={fallback}
      redirectTo="/"
    >
      {children}
    </PermissionGuard>
  );
}

// User Management (Phase 4.2)
interface UserManageGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function UserManageGuard({ children, fallback }: UserManageGuardProps) {
  return (
    <PermissionGuard
      permissions={["manage_users", "user.manage_all", "user.manage_team"]}
      requireAll={false} // OR logic - any permission works
      fallback={fallback}
      redirectTo="/app/dashboard"
    >
      {children}
    </PermissionGuard>
  );
}

// Project Management (Phase 4.3)
interface ProjectEditGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function ProjectEditGuard({ children, fallback }: ProjectEditGuardProps) {
  return (
    <PermissionGuard 
      permissions={["edit_projects", "manage_projects", "project.manage_all", "project.manage_team"]}
      requireAll={false} // OR logic - any of these permissions works
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
}

interface ProjectViewGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function ProjectViewGuard({ children, fallback }: ProjectViewGuardProps) {
  return (
    <PermissionGuard 
      permissions={["view_projects", "project.view_all", "project.view_team"]}
      requireAll={false} // OR logic - any view permission works
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
}

// Navigation & UI (Phase 4.4)
interface AdminGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function AdminGuard({ children, fallback }: AdminGuardProps) {
  return (
    <PermissionGuard 
      permissions={["manage_settings", "user.manage_all", "settings.admin", "system.override", "tenant.manage"]}
      requireAll={false} // OR logic - any admin permission works
      fallback={fallback}
      redirectTo="/app/dashboard"
    >
      {children}
    </PermissionGuard>
  );
}

// Additional guards for permissions mentioned in your data

interface TrackingGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function TrackingGuard({ children, fallback }: TrackingGuardProps) {
  return (
    <PermissionGuard 
      permission="reporting.admin" // Using reporting.admin as tracking permission doesn't exist yet
      fallback={fallback}
      redirectTo="/"
    >
      {children}
    </PermissionGuard>
  );
}

interface VideoManageGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function VideoManageGuard({ children, fallback }: VideoManageGuardProps) {
  return (
    <PermissionGuard 
      permissions={["manage_settings", "user.manage_all"]} // Using admin permissions until manage_videos is created
      requireAll={false}
      fallback={fallback}
      redirectTo="/"
    >
      {children}
    </PermissionGuard>
  );
}

interface PlaylistManageGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function PlaylistManageGuard({ children, fallback }: PlaylistManageGuardProps) {
  return (
    <PermissionGuard 
      permissions={["manage_settings", "user.manage_all"]} // Using admin permissions until manage_videos is created
      requireAll={false}
      fallback={fallback}
      redirectTo="/"
    >
      {children}
    </PermissionGuard>
  );
}

// Team-specific guards based on your new team permissions
interface TeamOwnerGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function TeamOwnerGuard({ children, fallback }: TeamOwnerGuardProps) {
  return (
    <PermissionGuard 
      permission="team.own"
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
}

interface TeamAdminGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function TeamAdminGuard({ children, fallback }: TeamAdminGuardProps) {
  return (
    <PermissionGuard 
      permissions={["team.admin", "team.own"]}
      requireAll={false} // Either team admin or team owner
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
}

interface TeamMemberManageGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function TeamMemberManageGuard({ children, fallback }: TeamMemberManageGuardProps) {
  return (
    <PermissionGuard 
      permission="team.member.manage"
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
}

// Superadmin guard for ultimate access
interface SuperadminGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function SuperadminGuard({ children, fallback }: SuperadminGuardProps) {
  return (
    <PermissionGuard 
      permissions={["system.override", "tenant.manage"]}
      requireAll={false} // Either permission indicates superadmin level
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
}

// Pipeline management guards
interface PipelineManageGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function PipelineManageGuard({ children, fallback }: PipelineManageGuardProps) {
  return (
    <PermissionGuard 
      permissions={["edit_pipeline", "edit_commercial_pipeline", "pipeline.manage_all", "team.pipeline.manage"]}
      requireAll={false}
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
}

interface PipelineViewGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function PipelineViewGuard({ children, fallback }: PipelineViewGuardProps) {
  return (
    <PermissionGuard 
      permissions={["view_pipeline", "view_commercial_pipeline", "pipeline.manage_all", "team.pipeline.manage"]}
      requireAll={false}
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
}

// Export the base component and all specialized guards
export {
  PermissionGuard as default,
};

// Utility component for conditionally rendering content based on permissions
interface ConditionalRenderProps {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  children: ReactNode;
  fallback?: ReactNode;
}

export function ConditionalRender({
  permission,
  permissions = [],
  requireAll = false,
  children,
  fallback = null,
}: ConditionalRenderProps) {
  return (
    <PermissionGuard
      permission={permission}
      permissions={permissions}
      requireAll={requireAll}
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
}
